import { useEffect, useState } from 'react'
import axios from 'axios'
import Modal from 'react-bootstrap/Modal'
import posthog from 'posthog-js'
import { useDispatch, useSelector } from 'react-redux'

import SupportChatModal from 'components/modals/supportChatModal'
import { RootState } from 'store/redux'
import { toggleSupportModal } from 'store/redux/sidebar'

interface IModalProps {
    show: boolean
    onHide: () => void
}

/** Which of the five routes out of this modal the user took. */
type TChannel = 'call' | 'faq' | 'eitaa' | 'telegram' | 'bot'

// Ten screens render their own <SupportModal> off one redux flag, so on the
// routes where a page and its layout both do it two instances see the same
// flip. One human action, one event: whichever instance gets there first
// reports, and closing arms the next one.
let reportedOpen = false

const SupportModal = ({ show, onHide }: IModalProps) => {
    // The chatbot lives here rather than in the layout so this modal owns the
    // whole "contact support" flow: it can hand off to the bot and the bot can
    // hand back when a human is needed.
    const [showChat, setShowChat] = useState(false)
    const dispatch = useDispatch()
    const source = useSelector(
        (state: RootState) => state.sideBarControler.supportModalSource
    )

    useEffect(() => {
        if (!show) {
            reportedOpen = false
            return
        }
        if (reportedOpen) return
        reportedOpen = true
        posthog.capture('support_options_opened', { source })
    }, [show, source])

    const handleWebhook = async (platform: string, channel: TChannel) => {
        // The single choke point for all five options, so the choice is
        // counted here rather than five times in the JSX.
        posthog.capture('support_channel_chosen', { channel })
        const profile = JSON.parse(localStorage.getItem('profile')!)
        const data: {
            user_name: string
            user_phone_number: number
            platform: string
        } = {
            user_name:
                profile && profile?.first_name + ' ' + profile?.last_name,
            user_phone_number:
                profile && Number('0' + String(profile?.phone_number.slice(3))),
            platform,
        }
        axios.post(
            'https://support.gethamyan.ir/webhook/monitoring-users-messages',
            { ...data }
        )
    }
    return (
        <>
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <div className=" w-full text-center py-4 text-xs">
                <p className=" mb-3 px-3">
                    از ساعت 10:30 الی 15 از طریق تماس تلفنی و از ساعت 9 تا 17 در پیام رسان‌ها، پاسخ‌گوی شما عزیزان هستیم.
                </p>
                <hr className=" mb-3" />
                <a
                    className=" text-green-1 my-3 no-underline"
                    onClick={() => {
                        handleWebhook('Call', 'call')
                        onHide()
                    }}
                    href="tel:02191070174"
                >
                    تماس تلفنی
                </a>
                <hr className=" mt-3" />
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        handleWebhook('pwa | faq', 'faq')
                        onHide()
                    }}
                    onClickCapture={() =>
                        window.location.replace(
                            'https://hamyanapp.com/blog/faq'
                        )
                    }
                >
                    سوالات متداول
                </p>
                <hr />
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        handleWebhook('eitta', 'eitaa')
                        onHide()
                        window.location.replace('https://eitaa.com/Hamyanapp')
                    }}
                >
                    پیام در ایتا
                </p>
                <hr />
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        // The webhook label stayed 'whatsapp' after this option
                        // became Telegram; support's dashboard keys off it, so
                        // only the analytics side is told the truth.
                        handleWebhook('whatsapp', 'telegram')
                        onHide()
                        window.location.replace(
                            'https://t.me/hamyanapp'
                        )
                    }}
                >
                    پیام در تلگرام
                </p>
                <hr />
                {/* Deliberately the LAST option, not the first: this is a trial
                    and the human routes should stay the obvious path until the
                    team has used it enough. Moving it up is a one-line change. */}
                <p
                    className=" text-green-1 my-3"
                    onClick={() => {
                        handleWebhook('pwa | ai chat', 'bot')
                        onHide()
                        setShowChat(true)
                        posthog.capture('support_bot_opened')
                    }}
                >
                    پرسش از پشتیبان هوشمند{' '}
                    <span className=" text-[10px] text-gray-400">
                        (آزمایشی)
                    </span>
                </p>
                <hr />
                <p className=" mt-4" onClick={onHide}>
                    انصراف
                </p>
            </div>
        </Modal>

        <SupportChatModal
            show={showChat}
            onHide={() => setShowChat(false)}
            onWantsHuman={() => {
                setShowChat(false)
                dispatch(
                    toggleSupportModal({
                        isOpenSupportModal: true,
                        source: 'bot_escalation',
                    })
                )
            }}
        />
        </>
    )
}

export default SupportModal
